/**
 * Lobby state. Host-authoritative like everything else: guests send intents,
 * the host decides, and the resulting state is broadcast whole.
 *
 * Deliberately free of DOM and transport concerns so the rules (team capacity,
 * side switching, who may start) can be tested without a browser.
 */
import type { Side } from '@office/sim';

export interface LobbyPlayer {
  id: string;
  name: string;
  side: Side;
  ready: boolean;
  /** True for the one player who runs the sim. */
  host: boolean;
}

export interface LobbyState {
  room: string;
  /** Id from the app's game registry. Only volley exists so far. */
  game: string;
  /** Players allowed per side: 1, 2 or 3. */
  teamSize: number;
  phase: 'lobby' | 'playing';
  players: LobbyPlayer[];
}

export interface LobbyIntent {
  name?: string;
  side?: Side;
  ready?: boolean;
}

export const TEAM_SIZES = [1, 2, 3] as const;

export function countSide(state: LobbyState, side: Side): number {
  return state.players.filter((p) => p.side === side).length;
}

/** The side with room, preferring the emptier one. */
function bestSide(state: LobbyState): Side {
  const left = countSide(state, 0);
  const right = countSide(state, 1);
  if (right < left && right < state.teamSize) return 1;
  if (left < state.teamSize) return 0;
  return right < state.teamSize ? 1 : 0;
}

export class LobbyHost {
  readonly state: LobbyState;

  constructor(room: string, hostId: string, opts: { game?: string; teamSize?: number; hostName?: string } = {}) {
    this.state = {
      room,
      game: opts.game ?? 'volley',
      teamSize: opts.teamSize ?? 1,
      phase: 'lobby',
      players: [
        {
          id: hostId,
          name: opts.hostName ?? 'Host',
          side: 0,
          ready: true,
          host: true,
        },
      ],
    };
  }

  get hostId(): string {
    return this.state.players[0]?.id ?? '';
  }

  add(id: string, name?: string): LobbyPlayer | null {
    if (this.state.players.some((p) => p.id === id)) return null;
    const player: LobbyPlayer = {
      id,
      name: name ?? `Player ${this.state.players.length + 1}`,
      side: bestSide(this.state),
      ready: false,
      host: false,
    };
    this.state.players.push(player);
    return player;
  }

  remove(id: string): boolean {
    const i = this.state.players.findIndex((p) => p.id === id);
    if (i <= 0) return false; // never drop the host
    this.state.players.splice(i, 1);
    return true;
  }

  /** Apply a guest's request. Returns true if anything actually changed. */
  applyIntent(id: string, intent: LobbyIntent): boolean {
    const player = this.state.players.find((p) => p.id === id);
    if (!player) return false;
    let changed = false;

    if (typeof intent.name === 'string') {
      const name = intent.name.trim().slice(0, 16);
      if (name && name !== player.name) {
        player.name = name;
        changed = true;
      }
    }
    if (intent.side !== undefined && intent.side !== player.side) {
      // A side switch only lands if the target team has a free spot.
      if (countSide(this.state, intent.side) < this.state.teamSize) {
        player.side = intent.side;
        changed = true;
      }
    }
    if (intent.ready !== undefined && intent.ready !== player.ready) {
      player.ready = intent.ready;
      changed = true;
    }
    return changed;
  }

  /**
   * Shrinking is refused when it would leave players with nowhere to stand.
   * There is no spectator concept, so the host has to make room first.
   */
  canUseTeamSize(size: number): boolean {
    return this.state.players.length <= Math.max(1, Math.min(3, Math.round(size))) * 2;
  }

  setTeamSize(size: number): boolean {
    const clamped = Math.max(1, Math.min(3, Math.round(size)));
    if (clamped === this.state.teamSize) return false;
    if (!this.canUseTeamSize(clamped)) return false;
    this.state.teamSize = clamped;
    // Shrinking can leave a side over capacity: move the newest players off it.
    for (const side of [0, 1] as Side[]) {
      const on = this.state.players.filter((p) => p.side === side);
      for (let i = clamped; i < on.length; i++) {
        const player = on[i]!;
        const otherSide: Side = side === 0 ? 1 : 0;
        if (countSide(this.state, otherSide) < clamped) player.side = otherSide;
      }
    }
    return true;
  }

  setGame(game: string): boolean {
    if (game === this.state.game) return false;
    this.state.game = game;
    return true;
  }

  setPhase(phase: LobbyState['phase']): void {
    this.state.phase = phase;
  }

  /** At least one player on each side, and nobody stranded off-team. */
  canStart(): boolean {
    return countSide(this.state, 0) >= 1 && countSide(this.state, 1) >= 1;
  }

  /**
   * Slot order for the match: host first, then join order. The sim's blob at
   * index i belongs to roster.ids[i].
   */
  roster(): { ids: string[]; sides: Side[]; names: string[] } {
    return {
      ids: this.state.players.map((p) => p.id),
      sides: this.state.players.map((p) => p.side),
      names: this.state.players.map((p) => p.name),
    };
  }

  snapshot(): LobbyState {
    return { ...this.state, players: this.state.players.map((p) => ({ ...p })) };
  }
}

/** Find a player's slot in the roster order the host published. */
export function slotOf(state: LobbyState, id: string): number {
  return state.players.findIndex((p) => p.id === id);
}
